<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  NCard, NForm, NFormItem, NInput, NInputNumber, NSwitch,
  NButton, NSpace, NText, NAlert, NCode, NCollapse, NCollapseItem, NSelect, NTabs, NTabPane,
} from 'naive-ui'
import { services, githubAPI, type Service, type ServiceInput, type RepositoryOnboardingDetection, type RepositoryOnboardingPreview, type RepositoryTemplateID } from '../api'
import { message } from '../message'
import {
  buildServiceSuggestions,
  buildSingleRepoWorkflow,
  openHrefInNewTab,
  resolveOnboardingImageRepo,
  syncAssistedField,
} from './serviceForm.helpers'
import { defaultOnboardingService, templateLabel, warningType } from './repositoryOnboarding'

type OnboardingMode = 'auto' | 'template' | 'skip'

const route = useRoute()
const router = useRouter()
const isOnboardingRoute = computed(() => route.path.endsWith('/onboarding'))
const editId = computed(() => {
  const v = route.params.id
  return v ? Number(v) : null
})
const isCreateMode = computed(() => editId.value == null)

const form = ref<ServiceInput>({
  name: '',
  desc: '',
  image_repo: '',
  domain: '',
  container_port: 8080,
  env_json: '{}',
  volumes_json: '[]',
  health_path: '',
  health_initial_delay_sec: 2,
  health_timeout_sec: 5,
  pre_deploy_cmd: '',
  post_deploy_cmd: '',
  hook_timeout_sec: 60,
  auto_deploy_on_push: false,
  auto_rollback: true,
})

const showOnboarding = ref(false)
const createdId = ref<number | null>(null)
const createdService = ref<Service | null>(null)
const onboardingMode = ref<OnboardingMode>('auto')
const repoSlug = ref('')
const applySecretSwitch = ref(true)
const commitWorkflowSwitch = ref(true)
const workflowBranch = ref('master')
const workflowPath = ref('.github/workflows/build.yml')
const branchOptions = ref<Array<{ label: string; value: string }>>([
  { label: 'master', value: 'master' },
  { label: 'main', value: 'main' },
])
const detectingBranches = ref(false)
const lastDetectedSlug = ref('')
const applyingGithubSecrets = ref(false)
const repositoryTemplate = ref<RepositoryTemplateID>('static-spa-nginx')
const repositoryDetection = ref<RepositoryOnboardingDetection | null>(null)
const repositoryPreview = ref<RepositoryOnboardingPreview | null>(null)
const detectingRepository = ref(false)
const previewingRepository = ref(false)
const applyingRepository = ref(false)
const addHealthzSwitch = ref(false)
const applyRepositorySecretSwitch = ref(true)
const goBuildPackage = ref('.')
const imageRepoTouched = ref(false)
const domainTouched = ref(false)
const saving = ref(false)
const baseURL = computed(() => `${location.protocol}//${location.host}`)
const suggestions = computed(() => buildServiceSuggestions(form.value.name))

watch(
  suggestions,
  (nextSuggestions) => {
    const imageRepoState = syncAssistedField(
      isCreateMode.value ? 'create' : 'edit',
      {
        value: form.value.image_repo ?? '',
        touched: imageRepoTouched.value,
      },
      nextSuggestions?.imageRepo ?? null,
    )
    form.value.image_repo = imageRepoState.value
    imageRepoTouched.value = imageRepoState.touched

    const domainState = syncAssistedField(
      isCreateMode.value ? 'create' : 'edit',
      {
        value: form.value.domain ?? '',
        touched: domainTouched.value,
      },
      nextSuggestions?.domain ?? null,
    )
    form.value.domain = domainState.value
    domainTouched.value = domainState.touched
  },
  { immediate: true },
)

const createdImageRepo = computed(() => resolveOnboardingImageRepo(createdService.value, form.value.image_repo))
const requiredSecrets = computed(() => ['SM_WEBHOOK_TOKEN'])

const workflowYaml = computed(() =>
  buildSingleRepoWorkflow({
    imageRepo: createdImageRepo.value.trim(),
    webhookURL: webhookURL.value,
  }),
)

const webhookURL = computed(() => `${baseURL.value}/api/webhooks/image`)

onMounted(async () => {
  if (editId.value != null) {
    try {
      const s = await services.get(editId.value)
      form.value = { ...s } as any
      if (isOnboardingRoute.value) {
        createdId.value = s.id
        createdService.value = s
        showOnboarding.value = true
      }
    } catch (e: any) {
      message.error(e.message || 'load failed')
    }
  }
})

function onImageRepoChange(value: string) {
  form.value.image_repo = value
  imageRepoTouched.value = true
}

function onDomainChange(value: string) {
  form.value.domain = value
  domainTouched.value = true
}

function applyImageRepoSuggestion() {
  if (!suggestions.value) return
  form.value.image_repo = suggestions.value.imageRepo
  imageRepoTouched.value = false
}

function applyDomainSuggestion() {
  if (!suggestions.value) return
  form.value.domain = suggestions.value.domain
  domainTouched.value = false
}

function clearDomain() {
  form.value.domain = ''
  domainTouched.value = true
}

async function save() {
  if (saving.value) {
    return
  }

  saving.value = true
  try {
    if (editId.value != null) {
      await services.update(editId.value, form.value)
      message.success('Service updated')
      router.push(`/services/${editId.value}`)
    } else {
      const created = await services.create(form.value)
      createdId.value = created.id
      createdService.value = created
      showOnboarding.value = true
    }
  } catch (e: any) {
    message.error(e.message || 'save failed')
  } finally {
    saving.value = false
  }
}

function finishOnboarding() {
  if (createdId.value != null) router.push(`/services/${createdId.value}`)
}

async function detectBranches() {
  const slug = repoSlug.value.trim()
  if (!slug || slug === lastDetectedSlug.value) return
  detectingBranches.value = true
  try {
    const info = await githubAPI.getOnboardingInfo(slug)
    lastDetectedSlug.value = slug
    const names = Array.from(new Set([...(info.branches || []), 'master', 'main']))
    branchOptions.value = names.map((n) => ({
      label: n === info.default_branch ? `${n}（default）` : n,
      value: n,
    }))
    if (info.default_branch) {
      workflowBranch.value = info.default_branch
    }
  } catch (e: any) {
    message.warning(`无法探测仓库分支：${e.message || e}（将使用默认 master/main）`)
  } finally {
    detectingBranches.value = false
  }
}

async function applyGithubSecrets() {
  if (createdId.value == null) {
    message.error('service 尚未创建，无法应用 GitHub 自动化')
    return
  }
  if (!repoSlug.value.trim()) {
    message.error('请先填写 GitHub 仓库 owner/repo')
    return
  }
  if (!applySecretSwitch.value && !commitWorkflowSwitch.value) {
    message.error('请至少打开一项：自动写入 Secret 或自动提交 workflow')
    return
  }
  if (commitWorkflowSwitch.value && !workflowBranch.value.trim()) {
    message.error('请填写目标分支')
    return
  }

  applyingGithubSecrets.value = true
  try {
    const result = await githubAPI.applyServiceSecrets(createdId.value, {
      repo_slug: repoSlug.value.trim(),
      apply_secret: applySecretSwitch.value,
      commit_workflow: commitWorkflowSwitch.value,
      workflow_branch: commitWorkflowSwitch.value ? workflowBranch.value.trim() : undefined,
      workflow_path: commitWorkflowSwitch.value ? (workflowPath.value.trim() || '.github/workflows/build.yml') : undefined,
      workflow_yaml: commitWorkflowSwitch.value ? workflowYaml.value : undefined,
    })
    const repo = result.repo_slug || repoSlug.value.trim()
    const parts: string[] = []
    if (result.secret_applied) parts.push(`Secret ${result.secret_name || 'SM_WEBHOOK_TOKEN'} 已写入`)
    if (result.workflow_committed) parts.push(`workflow 已提交到 ${result.workflow_branch}:${result.workflow_path}`)
    message.success(`${repo}: ${parts.join('；') || '操作成功'}`)
  } catch (e: any) {
    message.error(e.message || 'GitHub 自动化失败')
  } finally {
    applyingGithubSecrets.value = false
  }
}

function repositoryPayload() {
  const name = (createdService.value?.name || form.value.name || '').trim()
  const base = defaultOnboardingService(name)
  return {
    repo_slug: repoSlug.value.trim(),
    branch: workflowBranch.value.trim() || 'master',
    template_id: repositoryTemplate.value,
    service: {
      ...base,
      image_repo: createdImageRepo.value.trim() || base.image_repo,
      domain: (createdService.value?.domain || form.value.domain || base.domain || '').trim(),
      container_port: createdService.value?.container_port || form.value.container_port || 8080,
      health_path: createdService.value?.health_path || form.value.health_path || '',
      auto_deploy_on_push: Boolean(createdService.value?.auto_deploy_on_push ?? form.value.auto_deploy_on_push),
      auto_rollback: Boolean(createdService.value?.auto_rollback ?? form.value.auto_rollback ?? true),
      build_package: repositoryTemplate.value === 'go-http' ? (goBuildPackage.value.trim() || '.') : undefined,
    },
    add_healthz: addHealthzSwitch.value,
    apply_secret: applyRepositorySecretSwitch.value,
  }
}

async function previewRepositoryOnboarding() {
  if (!repoSlug.value.trim()) {
    message.error('请先填写 GitHub 仓库 owner/repo')
    return
  }
  previewingRepository.value = true
  try {
    repositoryPreview.value = await githubAPI.previewRepositoryOnboarding(repositoryPayload())
  } catch (e: any) {
    message.error(e.message || '生成预览失败')
  } finally {
    previewingRepository.value = false
  }
}

async function detectRepositoryOnboarding() {
  const slug = repoSlug.value.trim()
  if (!slug) {
    message.error('请先填写 GitHub 仓库 owner/repo')
    return
  }
  detectingRepository.value = true
  try {
    const name = (createdService.value?.name || form.value.name || '').trim()
    const detected = await githubAPI.detectRepositoryOnboarding(slug, workflowBranch.value.trim() || 'master', name)
    repositoryDetection.value = detected
    repositoryTemplate.value = detected.template_id
    if (detected.defaults?.health_path === '') {
      addHealthzSwitch.value = false
    }
    message.success(`已识别为 ${templateLabel(detected.template_id)}`)
  } catch (e: any) {
    message.error(e.message || '仓库检测失败')
  } finally {
    detectingRepository.value = false
  }
}

async function applyRepositoryOnboarding() {
  if (!repositoryPreview.value) {
    message.error('请先生成预览')
    return
  }
  if (repositoryPreview.value.blocking_errors?.length) {
    message.error('存在阻断错误，不能提交')
    return
  }
  applyingRepository.value = true
  try {
    const result = await githubAPI.applyRepositoryOnboarding(repositoryPayload())
    repositoryPreview.value = result.preview
    message.success(`${result.repo_slug}: 已提交 ${result.files_committed} 个文件到 ${result.branch}`)
  } catch (e: any) {
    message.error(e.message || '提交仓库接入文件失败')
  } finally {
    applyingRepository.value = false
  }
}

function openTokensInNewTab() {
  const { href } = router.resolve({ path: '/tokens' })
  openHrefInNewTab(href)
}
</script>

<template>
  <NAlert v-if="!showOnboarding" type="info" show-icon style="margin-bottom:16px">
    <template #header>{{ editId == null ? '新建服务' : '编辑服务' }}</template>
    <template v-if="editId == null">
      新建服务<b>不会</b>立即启动容器。保存后系统会：注册服务元数据 → 生成 Caddy 反代片段（若填了域名）→ 等待 CI 推送镜像（webhook）→ 触发首次部署。
    </template>
    <template v-else>
      修改服务元数据只更新数据库与 Caddy 片段，<b>不会</b>重启正在运行的容器。如需让改动生效，请在保存后到详情页点击「Restart」或重新「Deploy」。
    </template>
  </NAlert>

  <NCard :title="editId == null ? '新建服务（New service）' : '编辑服务（Edit service）'">
    <NForm label-placement="top" :model="form" :disabled="saving" v-if="!showOnboarding">
      <NFormItem label="服务名 Name（同时作为容器名与 URL slug，创建后不可修改）" required>
        <NInput v-model:value="form.name" placeholder="例如：blog" :disabled="editId != null" />
      </NFormItem>
      <NFormItem label="描述 Desc（可选，用一句话说明这个服务的用途）">
        <NInput v-model:value="form.desc" type="textarea" :rows="2" placeholder="例如：官网博客前端、订单 API、内部管理后台" />
      </NFormItem>
      <NFormItem label="镜像仓库 Image repo（CI 推送的镜像位置，不含 tag）" required>
        <NInput :value="form.image_repo" @update:value="onImageRepoChange" placeholder="例如：ghcr.io/owner/blog" />
        <NSpace v-if="suggestions?.imageRepo" align="center" size="small" style="margin-top:8px">
          <NText depth="3">建议值（当前实例自用约定）：<code>{{ suggestions.imageRepo }}</code></NText>
          <NButton size="small" quaternary :disabled="saving" @click="applyImageRepoSuggestion">应用建议</NButton>
        </NSpace>
        <NText v-else depth="3" style="display:block;margin-top:8px">填写合法 service name 后可生成当前部署实例的建议值</NText>
      </NFormItem>
      <NFormItem label="域名 Domain（留空则不接入 Caddy 反向代理，服务仅在 Docker 网络内可达）">
        <NInput :value="form.domain" @update:value="onDomainChange" placeholder="例如：blog.app.code2.top" />
        <NSpace v-if="suggestions?.domain" align="center" size="small" style="margin-top:8px">
          <NText depth="3">建议值（当前实例自用约定）：<code>{{ suggestions.domain }}</code></NText>
          <NButton size="small" quaternary :disabled="saving" @click="applyDomainSuggestion">应用建议</NButton>
          <NButton size="small" quaternary :disabled="saving" @click="clearDomain">清空</NButton>
        </NSpace>
        <NText v-else depth="3" style="display:block;margin-top:8px">填写合法 service name 后可生成当前部署实例的建议值</NText>
      </NFormItem>
      <NFormItem label="容器端口 Container port（容器内部监听的端口；Caddy 会反代到该端口）" required>
        <NInputNumber v-model:value="form.container_port" :min="1" :max="65535" />
      </NFormItem>

      <NCollapse>
        <NCollapseItem title="环境变量 Environment variables" name="env">
          <NFormItem label='环境变量 Env（JSON 对象，键值对会注入到容器，例如 {"DB_URL":"postgres://…"}）'>
            <NInput v-model:value="form.env_json" type="textarea" :rows="3" />
          </NFormItem>
        </NCollapseItem>
        <NCollapseItem title="挂载卷 Volumes" name="volumes">
          <NFormItem label='挂载卷 Volumes（JSON 数组，将宿主目录挂载到容器；mode 为 rw 或 ro）'>
            <NInput v-model:value="form.volumes_json" type="textarea" :rows="3"
              :placeholder='`例如：[{"host":"/srv/data","container":"/data","mode":"rw"}]`' />
          </NFormItem>
        </NCollapseItem>
        <NCollapseItem title="健康检查 Health checks" name="health">
          <NFormItem label="健康检查路径 Health path（部署后会以 HTTP GET 探测该路径，2xx 视为健康）">
            <NInput v-model:value="form.health_path" placeholder="/healthz" />
          </NFormItem>
          <NSpace>
            <NFormItem label="初始延迟 Initial delay (秒)（容器启动后等待多久开始探测健康）">
              <NInputNumber v-model:value="form.health_initial_delay_sec" :min="0" />
            </NFormItem>
            <NFormItem label="探测超时 Probe timeout (秒)（单次健康检查的最大耗时）">
              <NInputNumber v-model:value="form.health_timeout_sec" :min="1" />
            </NFormItem>
          </NSpace>
        </NCollapseItem>
        <NCollapseItem title="部署 Hook Deploy hooks" name="hooks">
          <NFormItem label="部署前命令 Pre-deploy command（sh -c，常用于数据库迁移；返回非零会中止部署）">
            <NInput v-model:value="form.pre_deploy_cmd" placeholder="例如：./bin/migrate.sh" />
          </NFormItem>
          <NFormItem label="部署后命令 Post-deploy command（sh -c，新容器健康后执行，用于通知、预热等）">
            <NInput v-model:value="form.post_deploy_cmd" />
          </NFormItem>
          <NFormItem label="Hook 超时 Hook timeout (秒)（pre/post-deploy 命令的最大耗时）">
            <NInputNumber v-model:value="form.hook_timeout_sec" :min="1" />
          </NFormItem>
        </NCollapseItem>
        <NCollapseItem title="部署策略 Deployment strategy" name="strategy">
          <NSpace>
            <NFormItem label="自动部署 Auto-deploy on webhook push（CI 推送新镜像时立即部署）">
              <NSwitch v-model:value="form.auto_deploy_on_push" />
            </NFormItem>
            <NFormItem label="自动回滚 Auto-rollback on failure（新版本健康检查失败时自动回到上一稳定版本）">
              <NSwitch v-model:value="form.auto_rollback" />
            </NFormItem>
          </NSpace>
        </NCollapseItem>
      </NCollapse>

      <NAlert type="warning" :show-icon="true" style="margin:12px 0">
        点击「保存」<b>不会</b>启动容器。{{ editId == null
          ? '保存后会进入 GitHub 接入面板：默认会自动把 SM_WEBHOOK_TOKEN（sm 内部管理的固定 token，对用户不可见）写入仓库 Secret，并把生成好的 workflow YAML 自动提交到所选分支。两步都可单独关闭。表单里的 image_repo / domain 建议值反映的是当前部署实例的自用约定，不是所有 sm 安装的通用默认值。'
          : '保存后修改只在下一次部署或重启容器时生效。' }}
      </NAlert>
      <NText depth="3" style="display:block;margin-bottom:12px">
        留空域名时，sm 不会为该服务配置宿主机端口发布；当前版本仅支持通过 Caddy 暴露外部访问入口。
      </NText>

      <NSpace>
        <NButton type="primary" :loading="saving" :disabled="saving" @click="save">保存 Save</NButton>
        <NButton @click="router.back()" :disabled="saving">取消 Cancel</NButton>
      </NSpace>
    </NForm>

    <div v-else>
      <NAlert type="success" :show-icon="true" style="margin-bottom:16px">
        <template #header>服务已创建</template>
        下一步是把这个服务接入 GitHub 构建流程。<code>SM_WEBHOOK_TOKEN</code> 由 sm 内部管理（固定、不过期、对用户不可见），下面的自动化会把它写入仓库 Secret；同时把 workflow YAML 提交到所选分支。<code>SM_WEBHOOK_URL</code> 和 <code>image_repo</code> 已内联在 workflow 里。
      </NAlert>

      <NCard title="GitHub 仓库" size="small" style="margin-bottom:16px">
        <NForm label-placement="top">
          <NFormItem label="仓库 owner/repo（workflow 与 Secret 都会写入这里）">
            <NInput v-model:value="repoSlug" placeholder="例如：owner/blog-app" @blur="detectBranches" />
            <NText depth="3" style="display:block;margin-top:6px">
              失焦后会自动探测该仓库的默认分支并填到下方下拉框。
            </NText>
          </NFormItem>
        </NForm>
      </NCard>

      <NCard title="接入方式 Onboarding mode" size="small" style="margin-bottom:16px">
        <NSpace>
          <NButton
            :type="onboardingMode === 'auto' ? 'primary' : 'default'"
            @click="onboardingMode = 'auto'"
          >
            自动接入（推荐）
          </NButton>
          <NButton
            :type="onboardingMode === 'template' ? 'primary' : 'default'"
            @click="onboardingMode = 'template'"
          >
            仅生成模板
          </NButton>
          <NButton
            :type="onboardingMode === 'skip' ? 'primary' : 'default'"
            @click="onboardingMode = 'skip'"
          >
            稍后配置
          </NButton>
        </NSpace>
      </NCard>

      <NAlert
        v-if="onboardingMode === 'auto'"
        type="info"
        :show-icon="true"
        style="margin-bottom:16px"
      >
        <template #header>自动接入</template>
        sm 会用你在「System → GitHub Integration」里保存的 PAT 调 GitHub API：把内部管理的 <code>SM_WEBHOOK_TOKEN</code> 写入仓库 Actions Secret，并把下方 workflow YAML 提交到所选分支。两步可分别用开关关闭，默认都打开。
      </NAlert>

      <NAlert
        v-if="onboardingMode === 'template'"
        type="info"
        :show-icon="true"
        style="margin-bottom:16px"
      >
        <template #header>仅生成模板</template>
        只展示 workflow YAML，<b>不</b>调用 GitHub API。你需要手动把 YAML 粘贴到目标仓库的 <code>.github/workflows/build.yml</code> 并提交；<code>SM_WEBHOOK_TOKEN</code> 也得自己到「System → GitHub Integration」复制 PAT 后手动配 Secret。一般情况下选「自动接入」更省事。
      </NAlert>

      <NAlert
        v-if="onboardingMode === 'skip'"
        type="warning"
        :show-icon="true"
        style="margin-bottom:16px"
      >
        「稍后配置」不会丢失这份服务的接入信息。你之后可以在服务详情页重新打开 GitHub Onboarding。
      </NAlert>

      <template v-if="onboardingMode !== 'skip'">
        <NCard title="所需配置 Required configuration" size="small" style="margin-bottom:16px">
          <ul style="margin:0; padding-left:20px; line-height:1.8">
            <li><code>SM_WEBHOOK_URL</code> 已内联到 workflow：<code>{{ webhookURL }}</code></li>
            <li><code>image_repo</code> 已内联到 workflow：<code>{{ createdImageRepo }}</code></li>
            <li>
              需要配置为 GitHub Secret 的列表（自动接入会自动写入，无需手动粘贴）：
              <template v-for="secret in requiredSecrets" :key="secret">
                <code style="margin-left:6px">{{ secret }}</code>
              </template>
            </li>
          </ul>
        </NCard>

        <NTabs type="line" animated>
          <NTabPane name="repository" tab="仓库模板接入">
            <NCard title="模板" size="small" style="margin-bottom:16px">
              <NForm label-placement="top">
                <NFormItem label="模板 Template">
                  <NSelect
                    v-model:value="repositoryTemplate"
                    :options="[
                      { label: templateLabel('static-spa-nginx'), value: 'static-spa-nginx' },
                      { label: templateLabel('go-http'), value: 'go-http' },
                      { label: templateLabel('existing-dockerfile'), value: 'existing-dockerfile' },
                    ]"
                    style="max-width:360px"
                  />
                </NFormItem>
                <NFormItem label="仓库检测">
                  <NButton :loading="detectingRepository" :disabled="detectingRepository" @click="detectRepositoryOnboarding">
                    Detect repository
                  </NButton>
                  <NText v-if="repositoryDetection" depth="3" style="display:block;margin-top:8px">
                    {{ repositoryDetection.project_kind }}；Dockerfile:
                    {{ repositoryDetection.has_dockerfile ? 'yes' : 'no' }}；workflow:
                    {{ repositoryDetection.has_workflow ? 'yes' : 'no' }}
                  </NText>
                </NFormItem>
                <NAlert
                  v-for="item in repositoryDetection?.evidence || []"
                  :key="item"
                  type="success"
                  show-icon
                  style="margin-bottom:8px"
                >
                  {{ item }}
                </NAlert>
                <NAlert
                  v-for="warn in repositoryDetection?.warnings || []"
                  :key="warn"
                  type="warning"
                  show-icon
                  style="margin-bottom:8px"
                >
                  {{ warn }}
                </NAlert>
                <NFormItem v-if="repositoryTemplate === 'static-spa-nginx'" label="静态站健康检查">
                  <NSwitch v-model:value="addHealthzSwitch" />
                  <NText depth="3" style="margin-left:12px">默认不设置 health path；打开后生成 /healthz。</NText>
                </NFormItem>
                <NFormItem v-if="repositoryTemplate === 'go-http'" label="Go build package">
                  <NInput v-model:value="goBuildPackage" placeholder="例如：. 或 ./cmd/api" style="max-width:360px" />
                  <NText depth="3" style="display:block;margin-top:6px">生成的 Dockerfile 会执行 go build -o /out/app 这个 package。</NText>
                </NFormItem>
                <NFormItem label="自动写入 SM_WEBHOOK_TOKEN 到仓库 Secret">
                  <NSwitch v-model:value="applyRepositorySecretSwitch" />
                </NFormItem>
                <NSpace>
                  <NButton :loading="previewingRepository" :disabled="previewingRepository" @click="previewRepositoryOnboarding">
                    Preview changes
                  </NButton>
                  <NButton
                    type="primary"
                    :loading="applyingRepository"
                    :disabled="applyingRepository || !repositoryPreview || !!repositoryPreview.blocking_errors?.length"
                    @click="applyRepositoryOnboarding"
                  >
                    Commit these files to {{ workflowBranch || 'master' }}
                  </NButton>
                </NSpace>
              </NForm>
            </NCard>

            <template v-if="repositoryPreview">
              <NAlert
                v-for="err in repositoryPreview.blocking_errors"
                :key="err"
                type="error"
                show-icon
                style="margin-bottom:8px"
              >
                {{ err }}
              </NAlert>
              <NAlert
                v-for="warn in repositoryPreview.warnings"
                :key="warn"
                :type="warningType(warn)"
                show-icon
                style="margin-bottom:8px"
              >
                {{ warn }}
              </NAlert>
              <NCard
                v-for="file in repositoryPreview.files"
                :key="file.path"
                :title="file.path"
                size="small"
                style="margin-bottom:12px"
              >
                <NText depth="3" style="display:block;margin-bottom:8px">
                  {{ file.exists ? '将覆盖已有文件' : '将创建新文件' }}
                </NText>
                <NCode :code="file.content" language="text" word-wrap />
              </NCard>
            </template>
          </NTabPane>

          <NTabPane name="workflow" tab="仅 workflow">
            <NAlert type="info" show-icon style="margin-bottom:16px">
              该模式只提交 workflow 和 Secret，不生成 Dockerfile 或 nginx.conf。
            </NAlert>
            <NCard v-if="onboardingMode === 'auto'" title="自动接入选项" size="small" style="margin-bottom:16px">
              <NForm label-placement="top">
                <NFormItem label="自动写入 SM_WEBHOOK_TOKEN 到仓库 Secret">
                  <NSwitch v-model:value="applySecretSwitch" />
                  <NText depth="3" style="margin-left:12px">关闭则跳过这一步，你需要自行在仓库里配置 Secret。</NText>
                </NFormItem>
                <NFormItem label="自动把 workflow YAML 提交到目标分支">
                  <NSwitch v-model:value="commitWorkflowSwitch" />
                  <NText depth="3" style="margin-left:12px">关闭则只展示 YAML，不调用 Contents API。</NText>
                </NFormItem>
                <NFormItem v-if="commitWorkflowSwitch" label="目标分支 Workflow branch（仓库里已有同名文件会被覆盖）">
                  <NSelect
                    v-model:value="workflowBranch"
                    :options="branchOptions"
                    tag
                    filterable
                    :loading="detectingBranches"
                    :placeholder="detectingBranches ? '探测中…' : '默认 master，可自行编辑'"
                    style="max-width:320px"
                  />
                </NFormItem>
                <NFormItem v-if="commitWorkflowSwitch" label="workflow 文件路径">
                  <NInput v-model:value="workflowPath" placeholder=".github/workflows/build.yml" />
                </NFormItem>
                <NSpace>
                  <NButton
                    type="primary"
                    :loading="applyingGithubSecrets"
                    :disabled="applyingGithubSecrets"
                    @click="applyGithubSecrets"
                  >
                    Apply
                  </NButton>
                  <NButton :disabled="applyingGithubSecrets" @click="finishOnboarding">完成 / 打开服务详情</NButton>
                </NSpace>
              </NForm>
            </NCard>

            <div style="margin:16px 0">
              <NText depth="3">
                <template v-if="onboardingMode === 'auto'">
                  下方是即将自动提交到仓库
                  <code v-if="repoSlug.trim()">{{ repoSlug.trim() }}</code>
                  的 workflow YAML（路径 <code>{{ workflowPath }}</code>）。已存在的同名文件会被覆盖。
                </template>
                <template v-else>
                  请把下面的 workflow 粘贴到
                  <code>.github/workflows/build.yml</code>
                  <template v-if="repoSlug.trim()">
                    （仓库：<code>{{ repoSlug.trim() }}</code>）
                  </template>
                  ，并到「System → GitHub Integration」复制 PAT 后手动配置 <code>SM_WEBHOOK_TOKEN</code> Secret。
                </template>
              </NText>
            </div>

            <NCode :code="workflowYaml" language="yaml" word-wrap />
          </NTabPane>
        </NTabs>
      </template>

      <NSpace style="margin-top:16px">
        <NButton type="primary" @click="finishOnboarding">打开服务详情</NButton>
        <NButton @click="openTokensInNewTab">新标签打开 Tokens</NButton>
      </NSpace>
    </div>
  </NCard>
</template>
